import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { LeftArrowWhite } from "../../assets/images/svg/LeftArrow";
import LcStartImg from "../../assets/images/LcStartImg.png";
import Webcam from "react-webcam";
import { applicationStore } from 'src/store/ApplicationStore';
import { toast } from 'react-toastify';
import logger from 'src/utils/logger';
import { observer } from 'mobx-react';
import { CameraPermissionStatuses } from 'src/enums/CameraPermissionStatuses';
import { LivenessHandler } from './handlers/livenessHandler';
import { SelfieOnlyHandler } from './handlers/selfieOnlyHandler';
import { FilesetResolver, FaceDetector } from '@mediapipe/tasks-vision';
import { getOrientationType } from 'src/helpers/getOrientationType';
import { useTranslation } from 'react-i18next';
import { FLOW_SHADOW, FLOW_VAR } from 'src/theme/flowColorTokens';

const portraitConstraints = {
  width: { min: 480, ideal: 720, max: 1080 },
  height: { min: 640, ideal: 960, max: 1440 },
  facingMode: "user",
};

const landscapeConstraints = {
  width: { min: 640, ideal: 960, max: 1440 },
  height: { min: 480, ideal: 720, max: 1080 },
  facingMode: "user",
};

const Selfie = observer(({ processType = 'liveness' }) => {
  const PAGE_PATH_NAME = "/selfie";
  const navigate = useNavigate();
  const { t } = useTranslation();
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);
  const faceDetectorRef = useRef(null);
  const hasProcessed = useRef(false);
  const intervalRef = useRef(null);
  const [cameraPermission, setCameraPermission] = useState("prompt");
  const [image, setImage] = useState(null);
  const [keep, setKeep] = useState(false);
  const [information, setInformation] = useState(t('selfie.info.lookForwardCenter'));
  const [orientation, setOrientation] = useState(getOrientationType());
  const [videoConstraints, setVideoConstraints] = useState(getOrientationType() === 'landscape' ? landscapeConstraints : portraitConstraints);
  const [loading, setLoading] = useState(false);
  const [wasmDependencyLoading, setWasmDependencyLoading] = useState(true);
  const [showPreloader, setShowPreloader] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const captureTimeoutRef = useRef(null);

  const handlerMap = {
    liveness: new LivenessHandler(navigate),
    selfieOnly: new SelfieOnlyHandler(navigate),
  };

  const handleOrientationChange = () => {
    const newOrientation = getOrientationType();
    setOrientation(newOrientation);
    setVideoConstraints(newOrientation === 'landscape' ? landscapeConstraints : portraitConstraints);
  };

  const capture = useCallback(() => {
    const webcam = webcamRef.current;
    const video = webcam?.video;

    if (!webcam) {
      logger.log(t('common.webcamRefNull'));
      return;
    }
    if (!video || video.readyState !== 4) {
      logger.log("Webcam not ready for screenshot yet.");
      return;
    }

    setShowPreloader(true);

    if (captureTimeoutRef.current) {
      clearTimeout(captureTimeoutRef.current);
    }

    captureTimeoutRef.current = setTimeout(() => {
      const imageSrc = webcam.getScreenshot();
      if (!imageSrc) {
        logger.log("Webcam did not capture an image.");
        setShowPreloader(false);
        return;
      }
      setImage(imageSrc);
      applicationStore.setSelfie(imageSrc);
    }, 300);
  }, [webcamRef, t]);

  const startCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    let counter = 3;
    setCountdown(counter);

    const newIntervalId = setInterval(() => {
      if (counter > 1) {
        counter -= 1;
        setCountdown(counter);
      } else {
        clearInterval(newIntervalId);
        setCountdown(null);
        capture();
        setTimeout(() => {
          if (!image) {
            capture();
          }
        }, 1000);
        setInformation("Done!");
      }
    }, 1000);

    intervalRef.current = newIntervalId;
  }, [capture, image]);

  const stopCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setCountdown(null);
  }, []);

  const handleCameraAccessDenied = useCallback(() => {
    if (webcamRef.current?.video?.srcObject) {
      const stream = webcamRef.current.video.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach((track) => track.stop());
    }

    applicationStore.setCameraError(PAGE_PATH_NAME);
    navigate("/error-access-camera", {
      replace: true,
      state: {
        previousPage: PAGE_PATH_NAME,
      },
    });
  }, [navigate]);

  useEffect(() => {
    let intervalId;
    async function checkCameraPermission() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        if (videoDevices.length === 0) {
          setCameraPermission("no_camera");
          applicationStore.setCameraError(PAGE_PATH_NAME);
          navigate("/error-no-camera", {
            replace: true,
            state: {
              previousPage: PAGE_PATH_NAME,
            },
          });
          return;
        }

        if (navigator.permissions && navigator.permissions.query) {
          const permissionStatus = await navigator.permissions.query({ name: "camera" });

          setCameraPermission(permissionStatus.state);

          if (permissionStatus.state === CameraPermissionStatuses.ACCESS_DENIED) {
            handleCameraAccessDenied();
          }
          //not working on safari
          // permissionStatus.onchange = () => {
          //   setCameraPermission(permissionStatus.state);

          //   if (permissionStatus.state === CameraPermissionStatuses.ACCESS_DENIED) {
          //     handleCameraAccessDenied();
          //   }
          // };

          if (!intervalId) {
            intervalId = setInterval(async () => {
              const updatedStatus = await navigator.permissions.query({ name: "camera" });
              if (cameraPermission !== updatedStatus.state) {
                setCameraPermission(updatedStatus.state);
              }

              if (updatedStatus.state === CameraPermissionStatuses.ACCESS_DENIED) {
                handleCameraAccessDenied();
              }
            }, 2000);
          }
        } else {
          logger.warn("Permissions API not supported on browser.");
        }
      } catch (err) {
        logger.error("Permission query error:", err);
      }
    }
    checkCameraPermission();
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [navigate, handleCameraAccessDenied]);

  useEffect(() => {
    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, []);

  useEffect(() => {
    let detector;
    let animationFrameId;

    async function initDetector() {
      try {
        const filesetResolver = await FilesetResolver.forVisionTasks(
          '/mediapipe/tasks-vision/wasm'
        );

        detector = await FaceDetector.createFromOptions(filesetResolver, {
          baseOptions: {
            modelAssetPath: '/mediapipe/face_detection/blaze_face_short_range.tflite',
            delegate: "CPU",
          },
          runningMode: 'VIDEO',
          minDetectionConfidence: 0.5,
        });

        faceDetectorRef.current = detector;
        setWasmDependencyLoading(false);
        runDetection();
      } catch (e) {
        logger.error('Failed to load FaceDetector:', e);
      }
    }

    const runDetection = () => {
      const video = webcamRef.current?.video;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');

      if (!video || !ctx || !faceDetectorRef.current) return;

      const processFrame = async () => {
        if (!faceDetectorRef.current) {
          animationFrameId = requestAnimationFrame(processFrame);
          return;
        }

        if (!video || video.readyState < 2) {
          animationFrameId = requestAnimationFrame(processFrame);
          return;
        }

        if (video.videoWidth === 0 || video.videoHeight === 0) {
          animationFrameId = requestAnimationFrame(processFrame);
          return;
        }

        const nowInMs = Date.now();

        let result;
        try {
          result = await faceDetectorRef.current.detectForVideo(video, nowInMs);
        } catch (e) {
          logger.error("FaceDetector error:", e);
          animationFrameId = requestAnimationFrame(processFrame);
          return;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        ctx.save();
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setKeep(false);

        if (result.detections.length > 0) {
          const box = result.detections[0].boundingBox;
          const widthRatio = box.width / video.videoWidth;
          const heightRatio = box.height / video.videoHeight;

          const minSize = widthRatio > 0.3 && heightRatio > 0.3;
          const maxSize = widthRatio < 0.5 && heightRatio < 0.7;

          if (!minSize) {
            setInformation(t('selfie.info.getCloser'));
          } else if (!maxSize) {
            setInformation(t('selfie.info.getAway'));
          } else {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            const landmark = result.detections[0].keypoints[2];
            if (landmark) {
              const x = landmark.x;
              const y = landmark.y;

              const centerXOK = x > 0.35 && x < 0.7;
              const centerYTop = y < 0.2;
              const centerYBottom = y > 0.8;

              if (!centerXOK) {
                setInformation(t('selfie.info.centerByLine'));
              } else if (centerYTop) {
                setInformation(t('selfie.info.moveDown'));
              } else if (centerYBottom) {
                setInformation(t('selfie.info.moveUp'));
              } else {
                setKeep(true);
                setInformation(t('selfie.info.staySteady'));
              }
            }
          }
        }

        ctx.restore();
        animationFrameId = requestAnimationFrame(processFrame);
      };

      animationFrameId = requestAnimationFrame(processFrame);
    };
    if (cameraPermission === CameraPermissionStatuses.ACCESS_GRANTED) {
      initDetector();
    }

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (faceDetectorRef.current) {
        faceDetectorRef.current.close();
        faceDetectorRef.current = null;
      }
    };
  }, [cameraPermission]);

  useEffect(() => {
    const webcam = webcamRef.current;
    return () => {
      if (webcam?.video?.srcObject) {
        const stream = webcam.video.srcObject;
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
      }
    };
  }, []);
  
  useEffect(() => {
    if (keep) {
      startCountdown();
    } else {
      stopCountdown();
    }
  }, [keep]);

  useEffect(() => {
    if (webcamRef.current) {
      webcamRef.current.video.style.width = '100%';
      webcamRef.current.video.style.height = '100%';
      webcamRef.current.video.style.objectFit = 'cover';
    }
  }, [orientation]);

  useEffect(() => {
    if (image && !hasProcessed.current) {
      hasProcessed.current = true;
      setLoading(true);

      const uploadImage = async () => {
        setShowPreloader(true);
        try {
          const handler = handlerMap[processType];
          if (!handler) throw new Error(`No handler defined for process: ${processType}`);

          await handler.handle(image);
        } catch (error) {
          logger.error("Request failed:", error);
          toast.error(error?.message || t('upload.imageNotUploaded'));
          hasProcessed.current = false;
          setLoading(false);
        } finally {
          setShowPreloader(false);
        }
      };

      uploadImage();
    }
  }, [image]);

  return (
    <div className="absolute inset-0 grid grid-rows-12 h-full text-white">
      <div className="row-span-3 pt-[47px] bg-Gray950 z-50">
        <div className="grid grid-cols-3 px-[46px]">
          <Button
            className=""
            variant="primaryBack"
            onClick={() => {
              if (webcamRef.current?.video?.srcObject) {
                const stream = webcamRef.current.video.srcObject;
                const tracks = stream.getTracks();
                tracks.forEach((track) => track.stop());
              }
              navigate(`/?aid=${applicationStore.aidCode}`, { replace: true });
            }}
          >
            <LeftArrowWhite />
          </Button>
          <div className="flex justify-center items-center">
            <div className="w-[48px] h-[48px] mx-auto">
              <figure>
                <img
                  alt={t('common.lcStartImg')}
                  className=""
                  src={LcStartImg}
                />
              </figure>
            </div>
          </div>
        </div>
        <div className="text-h5 font-bold text-center mt-6 text-Gray50">{t('selfie.title')}</div>
      </div>
      <div className="row-span-9 grid grid-rows-24 bg-Gray950">
        <div className="relative row-span-11">
          <Webcam
            audio={false}
            ref={webcamRef}
            forceScreenshotSourceSize
            screenshotFormat="image/jpeg"
            className="absolute rounded-t-[16px] h-full w-full scale-x-[-1]"
          />
          <canvas
            ref={canvasRef}
            className="absolute h-full w-full top-0 left-0 hidden"
            width="400"
            height="300"
          ></canvas>
          {showPreloader && (
            <img
              src={applicationStore?.selfie}
              alt={t("common.captured")}
              className={`absolute rounded-t-[16px] h-full w-full scale-x-[-1] blur-md -z-10}`}
            />
          )}
        </div>
        <div className="absolute top-0 left-0 w-full h-full z-10">
          <div className="relative h-full w-full overflow-hidden">
            <div
              className="absolute left-1/2 bg-transparent rounded-full h-60 w-60 opacity-80"
              style={{
                boxShadow: FLOW_SHADOW.vignettePhoneWide,
                transform: "translate(-50%, -50%)",
                top: "42%",
              }}
            >
              {showPreloader ? (
                <div className="absolute z-20 w-full h-full flex items-center justify-center">
                  <div
                    className={`inline-block h-16 w-16 border-4 border-t-2 border-t-transparent border-Gray50 rounded-full animate-spin`}
                  ></div>
                </div>
              ) : (
                <div className="text-h1 font-bold text-Gray50 absolute z-20 w-full h-full flex items-center justify-center text-opacity-75">
                  {countdown}
                </div>
              )}
            </div>
            {keep || showPreloader ? (
              <div className="absolute left-1/2 top-42% opacity-100 transition-opacity duration-1000 ease-in-out transform -translate-x-1/2 -translate-y-1/2">
                <div
                  role="img"
                  aria-label="loading circle"
                  className="rotate-90"
                >
                  <svg width="280" height="280">
                    <circle
                      cx="140"
                      cy="140"
                      r="120"
                      fill="none"
                      stroke={FLOW_VAR.success}
                      strokeWidth="2px"
                      strokeDasharray="753.9822368615503 753.9822368615503"
                      strokeDashoffset="0"
                    ></circle>
                  </svg>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <div className="w-full row-span-13 z-20">
          <div className="pt-20 flex justify-center flex-col items-center">
            <div className="text-h5 font-bold text-center text-Gray50 pb-16">
              <div className="w-52 flex items-center justify-center">
                {(!loading && !wasmDependencyLoading && information !== "Done!") ? (
                  <span>{information === 'Done!' ? t('selfie.done') : information}</span>
                ) : wasmDependencyLoading ? (
                  <>
                    <span>{t('selfie.loading')}</span>
                    <div className="ml-2 flex items-center">
                      <div className="inline-block h-4 w-4 border-2 border-t-2 border-t-transparent border-white rounded-full animate-spin"></div>
                    </div>
                  </>
                ) : (
                  <span>{t('selfie.done')}</span>
                )}

                {loading && (
                  <div className="ml-2 flex items-center">
                    <div className="inline-block h-4 w-4 border-2 border-t-2 border-t-transparent border-white rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            </div>
            <div className="text-center"></div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default Selfie;
